
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { useToast } from '@/hooks/use-toast';
import { trackSession } from '@/services/appsflyer';

export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const checkFirstLaunch = async () => {
      try {
        if (Capacitor.isNativePlatform()) {
          const { value } = await Preferences.get({ key: 'hasLaunched' });
          if (!value) {
            await supabase.auth.signOut();
            await Preferences.set({ key: 'hasLaunched', value: 'true' });
          }

          // Load cached auth state (defensive parse; bad/partial values can crash the app)
          const { value: cachedAuth } = await Preferences.get({ key: 'authState' });
          if (cachedAuth) {
            try {
              const parsed = JSON.parse(cachedAuth);
              if (typeof parsed === 'boolean') {
                setIsAuthenticated(parsed);
              } else {
                await Preferences.remove({ key: 'authState' });
              }
            } catch (e) {
              console.warn('Invalid cached authState; clearing it.', e);
              await Preferences.remove({ key: 'authState' });
            }
          }
        }
      } catch (error) {
        console.error('First launch check error:', error);
      }
    };

    checkFirstLaunch();

    // Check initial auth state and set up refresh token handling
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const isAuth = !!session;
        setIsAuthenticated(isAuth);

        // Cache auth state for offline access
        if (Capacitor.isNativePlatform()) {
          await Preferences.set({
            key: 'authState',
            value: JSON.stringify(isAuth)
          });
        }

        if (session?.refresh_token) {
          const refreshInterval = setInterval(async () => {
            const { data: { session: refreshedSession }, error } = await supabase.auth.refreshSession();
            if (error) {
              console.error('Error refreshing session:', error);
              clearInterval(refreshInterval);
              await supabase.auth.signOut();
              setIsAuthenticated(false);
              if (Capacitor.isNativePlatform()) {
                await Preferences.set({
                  key: 'authState',
                  value: JSON.stringify(false)
                });
              }
            }
          }, 3600000); // Refresh token every hour

          return () => clearInterval(refreshInterval);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setIsAuthenticated(false);
        if (Capacitor.isNativePlatform()) {
          await Preferences.set({
            key: 'authState',
            value: JSON.stringify(false)
          });
        }
      }
    };

    initAuth();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event);
      const isAuth = !!session;
      setIsAuthenticated(isAuth);

      // Track session in AppsFlyer when user signs in
      if (isAuth && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
        trackSession();
      }
      
      // Cache auth state for offline access
      if (Capacitor.isNativePlatform()) {
        await Preferences.set({
          key: 'authState',
          value: JSON.stringify(isAuth)
        });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return { isAuthenticated };
}
